import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { bumpRefresh, inferScopesFromUrl } from '../lib/myworldRefreshBus';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const AuthContext = createContext(null);

// Global axios defaults
axios.defaults.timeout = 15000;
// Send the httpOnly access_token cookie on every request so page reloads
// keep the user logged in (like Amazon / most SaaS). Backend sets the
// cookie on /auth/login, /register and refreshes it on /auth/me.
axios.defaults.withCredentials = true;

// Token store. Primary = httpOnly cookie set by backend on /auth/login.
// Fallback = localStorage, used when 3rd-party cookies are blocked
// (Safari ITP, Brave, embedded preview iframes, strict privacy mode).
// Without this fallback, page reloads in restrictive browsers log the user
// out even though the JWT is still valid server-side.
const TOKEN_STORAGE_KEY = 'cw_access_token';

let _accessToken = null;
try {
  _accessToken = localStorage.getItem(TOKEN_STORAGE_KEY) || null;
} catch { /* localStorage unavailable (private mode) — fine, cookie still works */ }

// Request interceptor: attach Bearer token to every request
axios.interceptors.request.use((config) => {
  if (_accessToken && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${_accessToken}`;
  }
  return config;
});

// Retry on network errors
axios.interceptors.response.use(
  (response) => {
    // Live-update bus: every successful mutation broadcasts a refresh
    // event so subscribed components can refetch without a full reload.
    try {
      const method = String(response.config?.method || '').toLowerCase();
      const url = String(response.config?.url || '');
      const isMutation = ['post', 'put', 'patch', 'delete'].includes(method);
      // /auth/me + /auth/login / /auth/register fire on every render in
      // some flows — skip those to avoid feedback loops.
      const isAuthChurn = url.includes('/api/auth/me')
        || url.includes('/api/auth/login')
        || url.includes('/api/auth/register')
        || url.includes('/api/auth/refresh');
      if (isMutation && !isAuthChurn && response.status >= 200 && response.status < 300) {
        bumpRefresh(...inferScopesFromUrl(url));
      }
    } catch { /* never let bus errors break the app */ }
    return response;
  },
  async (error) => {
    const config = error.config;
    if (!config._retryCount) config._retryCount = 0;
    if (!error.response && config._retryCount < 2) {
      config._retryCount += 1;
      await new Promise(r => setTimeout(r, 1500 * config._retryCount));
      return axios(config);
    }
    return Promise.reject(error);
  }
);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const tokenRef = useRef(null);

  const setToken = (token) => {
    _accessToken = token;
    tokenRef.current = token;
    try {
      if (token) localStorage.setItem(TOKEN_STORAGE_KEY, token);
      else localStorage.removeItem(TOKEN_STORAGE_KEY);
    } catch { /* private mode */ }
  };

  const fetchUser = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`);
      const data = response.data;
      const { access_token, ...userData } = data;
      if (access_token) setToken(access_token);
      setUser(userData);
    } catch (error) {
      setUser(null);
      setToken(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUser(); }, [fetchUser]);

  // Cross-tab sync: if another tab logs in/out (localStorage token changes),
  // re-check identity here too — otherwise this tab keeps showing the
  // previous account's data until a manual reload.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === TOKEN_STORAGE_KEY && e.newValue !== _accessToken) {
        _accessToken = e.newValue || null;
        tokenRef.current = _accessToken;
        fetchUser();
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [fetchUser]);

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/api/auth/login`, { email, password });
    const data = response.data;
    if (data.access_token) setToken(data.access_token);
    setUser(data.user);
    return data.user;
  };

  const businessLogin = async (email, password) => {
    const response = await axios.post(`${API_URL}/api/business/login`, { email, password });
    const data = response.data;
    if (data.access_token) setToken(data.access_token);
    setUser(data.user);
    return { user: data.user, company: data.company };
  };

  const businessRegister = async (payload) => {
    const response = await axios.post(`${API_URL}/api/business/register`, payload);
    const data = response.data;
    if (data.access_token) setToken(data.access_token);
    setUser(data.user);
    return { user: data.user, company: data.company };
  };

  const register = async ({ name, email, password, phone }) => {
    const response = await axios.post(`${API_URL}/api/auth/register`, { name, email, password, phone });
    const data = response.data;
    if (data.access_token) setToken(data.access_token);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {});
    } catch (error) {
      console.error('Logout error:', error);
    }
    setUser(null);
    setToken(null);
  };

  const isAdmin = user?.role === 'admin' || user?.role === 'master';
  const isMaster = user?.role === 'master';
  const isBusiness = user?.role === 'business';
  const isVerified = user?.verified === true;
  const isAuthenticated = !!user;

  return (
    <AuthContext.Provider value={{
      user, loading, login, register, businessLogin, businessRegister, logout, fetchUser,
      isAdmin, isMaster, isBusiness, isVerified, isAuthenticated,
      refreshUser: fetchUser
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
